package com.vizio.redwolf.pay.api

import com.vizio.redwolf.pay.client.WalletAPIHttpClient
import com.vizio.redwolf.pay.model.AddCreditCardClientRequestInfo
import com.vizio.redwolf.pay.model.Card
import com.vizio.redwolf.pay.model.NewCreditCardInfo
import com.vizio.redwolf.pay.model.UpdateCreditCardInfo
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.ECommerceEnvironmentConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.MessageTokenUtil
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.Platform
import com.vizio.redwolf.utils.RedwolfErrorCodes
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.choosePayEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.model.PayFailureResponse
import com.vizio.redwolf.utils.model.VersionResponse
import com.vizio.redwolf.utils.platform
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.NoTransformationFoundException
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.request
import io.ktor.http.isSuccess
import io.ktor.util.decodeBase64String
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.js.JsExport

class VizioWallet(private val payEnv: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)) : Wallet {
    private val walletClient = WalletAPIHttpClient(payEnv = payEnv, appConfig = appConfig, clientConfig = clientConfig)
    private val messageTokenDecrypted = payEnv.messageTokenAESKeyMasked?.decodeBase64String()?.let { it.substring(0, it.length - 5).reversed() }

    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS))
    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(choosePayEnvironment(envConfig), appConfig, clientConfig)
    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS, timeout))

    companion object {
        const val ERROR_CONTENT_TYPE_NOT_SUPPORTED = "NoTransformationFoundException: Response ContentType is not supported"
    }

    private fun getMessageToken(nonce: String): String? {
        val messageToken = when (platform) {
            Platform.Android, Platform.iOS -> {
                messageTokenDecrypted?.let { aesKey ->
                    MessageTokenUtil().generateMessageToken(nonce, aesKey)
                }
            }

            Platform.JS -> {
                null
            }
        }
        return messageToken
    }

    override suspend fun getCreditCards(token: String, traceId: String?): WalletResult<List<Card>> {
        return retryWalletRequest {
            val httpResult = walletClient.getCreditCards(token, traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    override suspend fun addCreditCard(token: String, newCardInfo: NewCreditCardInfo, reCaptchaToken: String?, traceId: String?): WalletResult<Card> {
        val messageToken = getMessageToken(nonce = newCardInfo.cardNonce)
        val clientRequestInfo = AddCreditCardClientRequestInfo(
            cardNonce = newCardInfo.cardNonce,
            isDefault = newCardInfo.isDefault,
            billingAddress = newCardInfo.billingAddress,
            messageToken = messageToken,
            reCaptchaToken = reCaptchaToken
        )
        return retryWalletRequest {
            val httpResult = walletClient.addCreditCard(token, clientRequestInfo, traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    override suspend fun deleteCreditCard(token: String, cardToken: String, traceId: String?): WalletResult<Card> {
        return retryWalletRequest {
            val httpResult = walletClient.deleteCreditCard(cardToken, token, traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    override suspend fun getDefaultCreditCard(token: String, traceId: String?): WalletResult<Card> {
        return retryWalletRequest {
            val httpResult = walletClient.getDefaultCreditCard(token, traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    override suspend fun setDefaultCreditCard(token: String, cardToken: String, traceId: String?): WalletResult<Card> {
        return retryWalletRequest {
            val httpResult = walletClient.setDefaultCreditCard(cardToken, token, traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    override suspend fun updateCreditCard(token: String, cardToken: String, updateCreditCardInfo: UpdateCreditCardInfo, traceId: String?): WalletResult<Card> {
        return retryWalletRequest {
            val httpResult = walletClient.updateCreditCard(token, cardToken, updateCreditCardInfo, traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    /**
     * This token is used to display the Braintree drop-in UI
     */
    override suspend fun getBraintreeClientToken(traceId: String?): WalletResult<BrainTreeTokenResponse> {
        return retryWalletRequest {
            val httpResult = walletClient.getClientToken(traceId)
            handleWalletHttpResult(httpResult)
        }
    }

    override suspend fun getVersion(traceId: String?): WalletResult<VersionResponse> {
        return retryWalletRequest {
            val getVersionResponse = walletClient.getVersion(traceId)
            handleWalletHttpResult(getVersionResponse)
        }
    }


    private suspend inline fun <T : HttpResponse, reified R : Any> handleWalletHttpResult(httpResult: HttpResult<T>): WalletResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val statusCode = httpResponse.status.value
                val headers = ResponseHeaders.Valid(statusCode)
                val requestUrl: String = httpResponse.request.url.toString()
                if (httpResponse.status.isSuccess()) {
                    try {
                        WalletResult.Success(headers, httpResponse.body())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        WalletResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val failureMessage = httpResponse.body<PayFailureResponse>()
                        WalletResult.Failed(headers, response = failureMessage)
                    }catch (ex: NoTransformationFoundException) {
                        ex.printStackTrace()
                        val exception = Exception("$ERROR_CONTENT_TYPE_NOT_SUPPORTED:$requestUrl")
                        WalletResult.Error(headers, exception)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        WalletResult.Error(headers, ex)
                    }
                }
            }
            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                val exception = httpResult.exception
                if (exception is TimeoutException) {
                    WalletResult.Timeout(headers, exception)
                }
                else {
                    WalletResult.Error(headers, exception)
                }
            }
        }
    }

    private fun <T: Any> isWalletSuccess(result: T): Boolean {
        return result is WalletResult.Success<*>
    }

    private suspend inline fun <T: Any> retryWalletRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isWalletSuccess)
    }
}

@JsExport
sealed class WalletResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : WalletResult<T>()
    data class ClientError(val headers: ResponseHeaders, val internalErrorCode: RedwolfErrorCodes, val message: String) : WalletResult<Nothing>()
    data class Failed(val headers: ResponseHeaders, val response: PayFailureResponse) : WalletResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : WalletResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : WalletResult<Nothing>()

    fun <R> fold (
        onSuccess: (Success: Success<T>) -> R,
        onClientError: (ClientError: ClientError) -> R,
        onFailed: (Failed: Failed) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is ClientError -> onClientError(ClientError(this.headers, this.internalErrorCode, this.message))
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}

@Serializable
@JsExport
data class BrainTreeTokenResponse(@SerialName("token") val token: String)
